/**
 * motion.js
 * 
 * Handles scroll-triggered animations and motion effects throughout the site.
 * All functions respect the user's "prefers-reduced-motion" accessibility setting.
 * 
 * Key concepts:
 * - Elements with "motion-*" classes (e.g., motion-fade-in) are animated via CSS
 * - The "motion-paused" class prevents CSS animations from running (via animation-play-state: paused)
 * - When elements scroll into view, "motion-paused" is removed, triggering the animation
 * - This file coordinates when those classes are added/removed
 * 
 * Exported functions (called from app.js):
 * - motionAtIntersections(): Sets up viewport-based animation triggers
 * - heroAnimationSwap(): Manages the hero's two-phase animation (load → idle)
 * - countingNumbers(): Animates numeric counters when they become visible
 */

// Check user's motion preference once at load time
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

// Shared IntersectionObserver instance, created by motionAtIntersections()
// Other functions reference this to unobserve elements they manage directly
let motionObserver = null;

/**
 * Utility: Executes a callback when an element is no longer paused.
 * 
 * If the element doesn't have "motion-paused", the callback runs immediately.
 * Otherwise, a MutationObserver watches for the class to be removed (which
 * happens when the element scrolls into view via motionAtIntersections).
 * 
 * This pattern is needed because motionAtIntersections() adds "motion-paused"
 * synchronously, but the IntersectionObserver callback that removes it is async.
 * Functions that need to act when an element becomes visible use this helper.
 * 
 * @param {HTMLElement} el - Element to watch
 * @param {Function} callback - Function to call when element is unpaused
 */
function whenUnpaused(el, callback) {
    if (!el.classList.contains('motion-paused')) {
        callback();
    } else {
        const observer = new MutationObserver(() => {
            if (!el.classList.contains('motion-paused')) {
                callback();
                observer.disconnect();
            }
        });
        observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    }
}

/**
 * Sets up scroll-triggered animations for all elements with "motion-*" classes.
 * 
 * How it works:
 * 1. Finds all elements with classes like "motion-fade-in", "motion-scale-in", etc.
 * 2. Adds "motion-paused" to each, which CSS uses to pause their animations
 * 3. Creates an IntersectionObserver to watch when elements enter the viewport
 * 4. When an element becomes visible, removes "motion-paused" to trigger its animation
 * 5. Each element only animates once (unobserved after triggering)
 * 
 * If the user prefers reduced motion, this function exits early and animations
 * never get paused, so CSS can handle them appropriately (typically instant/no animation).
 */
export function motionAtIntersections() {
    // Skip entirely if user prefers reduced motion or browser lacks IntersectionObserver
    if (prefersReducedMotion.matches || !('IntersectionObserver' in window)) return;

    const motionElements = document.querySelectorAll('[class*="motion-"]');

    // Create observer that removes "motion-paused" when elements enter viewport
    motionObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
            if (entry.isIntersecting) {
                entry.target.classList.remove('motion-paused');
                motionObserver.unobserve(entry.target); // Only animate once
            }
        }
    });

    // Pause all motion elements and start observing them
    motionElements.forEach((el) => {
        el.classList.add('motion-paused');
        motionObserver.observe(el);
    });
}

/**
 * Manages the hero component's two-phase animation sequence.
 * 
 * The hero has two animation states controlled by CSS classes:
 * - "load": Initial entrance animations (elements fly/fade into position)
 * - "idle": Subtle floating/breathing animations that loop infinitely
 * 
 * This function waits for all "load" animations to complete, then swaps
 * the class to "idle" to begin the looping animations.
 * 
 * The hero also contains child elements with "motion-observe" class that
 * are managed by motionAtIntersections(). When swapping to idle, we need
 * to unobserve these so they don't interfere with the idle animations.
 * 
 * For users who prefer reduced motion, we skip directly to the idle state
 * so they see the final positioned elements without any animation.
 */
export function heroAnimationSwap() {
    const heroAnimation = document.querySelector('.hero-animation.load');
    if (!heroAnimation) return;

    /**
     * Transitions the hero from "load" to "idle" state.
     * Also cleans up any motion-observe children so they animate freely in idle.
     */
    const swapToIdle = () => {
        // Unobserve and unpause all child elements marked for observation
        // These need to be freed from the intersection observer to animate in idle state
        if (motionObserver) {
            heroAnimation.querySelectorAll('.motion-observe').forEach(el => {
                el.classList.remove('motion-paused');
                motionObserver.unobserve(el);
            });
        }
        heroAnimation.classList.replace('load', 'idle');
    };

    // For reduced motion users, skip animations entirely and show final state
    if (prefersReducedMotion.matches) {
        swapToIdle();
        return;
    }

    // Track concurrent animations using a counter
    // CSS animations on child elements bubble up, so we count starts and ends
    let animationCount = 0;

    const handleAnimationStart = () => animationCount++;
    const handleAnimationEnd = () => {
        // Decrement and check if all animations have completed
        if (--animationCount === 0) {
            swapToIdle();
            // Clean up listeners since we only need to swap once
            heroAnimation.removeEventListener('animationstart', handleAnimationStart);
            heroAnimation.removeEventListener('animationend', handleAnimationEnd);
        }
    };

    // Wait for hero to scroll into view (become unpaused) before listening for animations
    // This is necessary because motionAtIntersections() pauses all motion-* elements,
    // and the hero has "motion-opacity-in" which makes it start paused
    whenUnpaused(heroAnimation, () => {
        heroAnimation.addEventListener('animationstart', handleAnimationStart);
        heroAnimation.addEventListener('animationend', handleAnimationEnd);
    });
}

/**
 * Animates a number from a start value to an end value over a duration.
 * Used internally by countingNumbers() for the counting-up effect.
 * 
 * Features:
 * - Preserves decimal precision based on the end value
 * - Uses requestAnimationFrame for smooth 60fps animation
 * - Handles zero duration (shows end value immediately)
 * 
 * @param {HTMLElement} el - Element whose textContent will be updated
 * @param {number} start - Starting number
 * @param {number} end - Ending number
 * @param {number} duration - Animation duration in milliseconds
 */
function animateNumber(el, start, end, duration) {
    // Determine decimal precision from the input values
    const getDecimalPlaces = (n) => (String(n).split('.')[1] || '').length;
    const startDecimals = getDecimalPlaces(start);
    const endDecimals = getDecimalPlaces(end);
    const decimalPlaces = Math.max(startDecimals, endDecimals);
    
    // If end is a whole number but start has decimals, strip trailing ".0"
    const stripTrailingZero = endDecimals === 0 && decimalPlaces > 0;

    // For reduced motion (duration = 0), show final value immediately
    if (duration <= 0) {
        el.textContent = end;
        return;
    }

    let startTimestamp = null;

    const step = (timestamp) => {
        // Initialize start time on first frame
        startTimestamp ??= timestamp;
        
        // Calculate progress (0 to 1) and current value
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        const current = progress * (end - start) + start;
        
        // Format with appropriate decimal places
        const formatted = decimalPlaces ? current.toFixed(decimalPlaces) : Math.floor(current);
        el.textContent = stripTrailingZero ? formatted.replace(/\.0$/, '') : formatted;

        // Continue animation until complete
        if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
}

/**
 * Initializes counting animations for elements with data-count-end attribute.
 * 
 * HTML usage:
 *   <span data-count-end="100">0</span>
 *   <span data-count-start="50" data-count-end="100" data-count-duration="2000">50</span>
 * 
 * Data attributes:
 * - data-count-end (required): The final number to count to
 * - data-count-start (optional): Starting number, defaults to 0
 * - data-count-duration (optional): Animation duration in ms, defaults to 1500
 * 
 * If the counter is inside a motion-* element, it waits for that element
 * to scroll into view before starting. Otherwise, it starts immediately.
 * 
 * For users who prefer reduced motion, duration is set to 0 (instant).
 */
export function countingNumbers() {
    document.querySelectorAll('[data-count-end]').forEach((el) => {
        const start = parseFloat(el.dataset.countStart) || 0;
        const end = parseFloat(el.dataset.countEnd);
        const duration = prefersReducedMotion.matches ? 0 : (parseFloat(el.dataset.countDuration) || 1500);
        
        // Check if this counter is inside a motion-animated container
        const motionAncestor = el.closest('[class*="motion-"]');

        if (motionAncestor) {
            // Wait for the container to become visible before counting
            whenUnpaused(motionAncestor, () => animateNumber(el, start, end, duration));
        } else {
            // No motion container, start counting immediately
            animateNumber(el, start, end, duration);
        }
    });
}
